import React from 'react';
import { Box, Typography, Grid, TextField } from '@mui/material';

const FormulaVisualization = ({ activityType, calculationParams, handleParamChange, forestStands, standId }) => {
    const stand = forestStands?.features?.find(f => f.id === standId);
    if (!stand) return null;

    const area = stand?.properties?.area_hectares ? Number(stand.properties.area_hectares) : 0;
    const p = calculationParams;

    // Helper to format values for display without crashing or showing NaN
    const val = (v) => v === '' ? 0 : (v || 0);

    if (activityType === 'Final Felling') {
        return (
            <Box sx={{ mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: '4px', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="primary" fontWeight="bold">Automated Calculations (Volume-based)</Typography>
                <Typography variant="body2" sx={{ my: 1, fontStyle: 'italic' }}>
                    Revenue = (Yield * Timber% * Price) + (Yield * Pulp% * Price) + (Yield * GROT * Price)
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <TextField label="Yield (m3sk)" type="number" size="small" fullWidth value={p.yield ?? ''} onChange={(e) => handleParamChange('yield', e.target.value)} />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField label="Timber %" type="number" size="small" fullWidth value={p.timber_percentage ?? ''} onChange={(e) => handleParamChange('timber_percentage', e.target.value)} />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField label="Timber Price" type="number" size="small" fullWidth value={p.timber_price_m3sk ?? ''} onChange={(e) => handleParamChange('timber_price_m3sk', e.target.value)} />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField label="Pulpwood %" type="number" size="small" fullWidth value={p.pulpwood_percentage ?? ''} onChange={(e) => handleParamChange('pulpwood_percentage', e.target.value)} />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField label="Pulpwood Price" type="number" size="small" fullWidth value={p.pulpwood_price_m3sk ?? ''} onChange={(e) => handleParamChange('pulpwood_price_m3sk', e.target.value)} />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField label="GROT Factor" type="number" size="small" fullWidth value={p.grot_factor ?? ''} onChange={(e) => handleParamChange('grot_factor', e.target.value)} />
                    </Grid>
                </Grid>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Cost = {val(p.felling_start_cost)} + ({area.toFixed(2)} ha * {val(p.felling_hours_per_ha)} hr/ha * {val(p.hourly_machine_rate)} SEK/hr)
                </Typography>
            </Box>
        );
    }

    if (activityType === 'Planting') {
        return (
            <Box sx={{ mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: '4px', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="primary" fontWeight="bold">Planting Cost Calculation</Typography>
                <Typography variant="body2" sx={{ my: 1, fontStyle: 'italic' }}>
                    Cost = {area.toFixed(2)} ha * Density * Price
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField label="Density (plants/ha)" type="number" size="small" fullWidth value={p.planting_density_per_ha ?? ''} onChange={(e) => handleParamChange('planting_density_per_ha', e.target.value)} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Price/Plant" type="number" size="small" fullWidth value={p.cost_per_plant ?? ''} onChange={(e) => handleParamChange('cost_per_plant', e.target.value)} />
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (activityType === 'Clearing') {
        return (
            <Box sx={{ mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: '4px', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="primary" fontWeight="bold">Clearing Cost Calculation</Typography>
                <Typography variant="body2" sx={{ my: 1, fontStyle: 'italic' }}>
                    Cost = {area.toFixed(2)} ha * {val(p.clearing_rate_per_ha)} SEK/ha
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TextField label="Rate (SEK/ha)" type="number" size="small" fullWidth value={p.clearing_rate_per_ha ?? ''} onChange={(e) => handleParamChange('clearing_rate_per_ha', e.target.value)} />
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (activityType === 'Thinning') {
        return (
            <Box sx={{ mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: '4px', bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="primary" fontWeight="bold">Thinning Calculation (Yield: {val(p.thinning_yield)} m3sk)</Typography>
                <Typography variant="body2" sx={{ my: 1, fontStyle: 'italic' }}>
                    Revenue = Yield * Pulpwood Price
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField label="Yield (m3sk)" type="number" size="small" fullWidth value={p.thinning_yield ?? ''} onChange={(e) => handleParamChange('thinning_yield', e.target.value)} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Machine Rate" type="number" size="small" fullWidth value={p.hourly_machine_rate ?? ''} onChange={(e) => handleParamChange('hourly_machine_rate', e.target.value)} />
                    </Grid>
                </Grid>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Cost = {val(p.thinning_start_cost)} + ({area.toFixed(2)} ha * {val(p.thinning_hours_per_ha)} hr/ha * {val(p.hourly_machine_rate)} SEK/hr)
                </Typography>
            </Box>
        );
    }

    return null;
};

export default FormulaVisualization;
